import { Injectable } from "@angular/core";
import { NotificationsService } from "angular2-notifications";
import { BehaviorSubject, Observable } from "rxjs";
import { StorageServiceKey } from "./storage.service";

export interface QuoteProduct {
  id: number;
  name: string;
  image: string | null;
  price: number
}

@Injectable({
  providedIn: "root",
})
export class QuoteService {
  private readonly storageKey = StorageServiceKey.storageKey;

  private quoteSubject = new BehaviorSubject<QuoteProduct[]>(this.getAll());
  public quote$: Observable<QuoteProduct[]> = this.quoteSubject.asObservable();

  constructor(private notifications: NotificationsService) { }

  /** Get all products from localStorage */
  public getAll(): QuoteProduct[] {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || "[]");
    } catch {
      return [];
    }
  }

  /** Add product to quote */
  public add(product: QuoteProduct): void {
    const products = this.getAll();

    const exists = products.some(p => p.id === product.id);
    if (exists) {
      this.notifications.warn(
        "Already Added",
        "Product already exists in quote"
      );
      return;
    }

    products.push(product);
    localStorage.setItem(this.storageKey, JSON.stringify(products));
    this.quoteSubject.next(products);

    this.notifications.success(
      "Quote Updated",
      "Product added to quote"
    );
  }


  /** Remove product from quote */
  public remove(productId: number): void {
    const products = this.getAll().filter(p => p.id !== productId);

    localStorage.setItem(this.storageKey, JSON.stringify(products));
    this.quoteSubject.next(products);

    this.notifications.success(
      "Quote Updated",
      "Product removed from quote"
    );
  }

  /** Clear quote */
  public clear(): void {
    localStorage.removeItem(this.storageKey);
    this.quoteSubject.next([]);

    this.notifications.success(
      "Quote Cleared",
      "All products removed from quote"
    );
  }

  /** Check if product exists */
  public exists(productId: number): boolean {
    return this.getAll().some(p => p.id === productId);
  }

  /** Total items count */
  public count(): number {
    return this.getAll().length;
  }

  public emptyProducts(): void {
    localStorage.setItem(this.storageKey, JSON.stringify([]));
    this.quoteSubject.next([]);
  }

}
